"use client";

import React, { useEffect } from "react";

import { storeUserRoles } from "@punchup/analytics/user-roles";
import { useIsAuthorizedToSeeDashboards } from "@punchup/venues/client";
import useIsSuperAdmin from "@/hooks/useIsSuperAdmin";

/**
 * Derives the current user's role flags from the app's existing role lookups
 * (super-admin check + managed comedians/venues) and persists them so that
 * `trackEvent` can attach `is_super_admin` / `is_venue_manager` /
 * `is_comedian_manager` to every Mixpanel + Statsig event.
 *
 * Mounted once at the app root. Reuses `useIsSuperAdmin` and
 * `useIsAuthorizedToSeeDashboards` — no new queries or endpoints. The flags are
 * cleared on logout by `resetAnalytics`.
 */
export function UserRolesProvider({ children }: { children: React.ReactNode }) {
  const isSuperAdmin = useIsSuperAdmin(); // boolean | undefined while loading
  const { comediansAuthorized, venuesAuthorized, isLoading } =
    useIsAuthorizedToSeeDashboards();

  useEffect(() => {
    // Only persist once both role sources have resolved, so we never overwrite
    // known-true flags with a transient all-false snapshot during load.
    if (isSuperAdmin === undefined || isLoading) return;
    storeUserRoles({
      is_super_admin: isSuperAdmin === true,
      is_venue_manager: venuesAuthorized.length > 0,
      is_comedian_manager: comediansAuthorized.length > 0,
    });
  }, [isSuperAdmin, isLoading, comediansAuthorized, venuesAuthorized]);

  return <>{children}</>;
}
